<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { gsap } from "gsap";
import { useGlobalStore } from "@stores/global";
import IconX from "@components/icons/X.vue";
import IconPlay from "@components/icons/Play.vue";

const globalStore = useGlobalStore();

const rootEl = ref(null);
const clips = computed(() => globalStore.btsVideos);

function close() {
    globalStore.hidePanel("behindTheScenes");
}

function openClip(clip) {
    globalStore.setModal(["clip", { clip }, true, true, true]);
}

function playReveal() {
    if (!rootEl.value) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const targets = rootEl.value.querySelectorAll("[data-reveal]");
    if (!targets.length) return;

    gsap.killTweensOf(targets);
    gsap.fromTo(
        targets,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", stagger: 0.06 },
    );
}

watch(
    () => globalStore.panelActive("behindTheScenes"),
    async (active) => {
        if (!active) return;
        await globalStore.loadBTSVideos();
        await nextTick();
        playReveal();
    },
    { immediate: true },
);
</script>

<template>
    <Transition name="slide-left" appear>
        <div
            v-if="
                globalStore.panelActive('behindTheScenes') &&
                globalStore.activePage === 'explore'
            "
            ref="rootEl"
            class="behind-the-scenes bg-marine-800 border-marine-500 flex flex-col gap-10 overflow-y-auto border-r p-6"
        >
            <div data-reveal class="flex items-center justify-between">
                <p
                    class="text-[18px] leading-[0.9] font-bold tracking-[-0.02em] uppercase"
                >
                    Behind the scenes
                </p>
                <button
                    class="text-marine-800 border-marine-600 flex size-8 shrink-0 items-center justify-center rounded-sm border-2 bg-yellow-400 shadow-[0_4px_0_0_var(--color-marine-600)]"
                    @click="close"
                    aria-label="Close"
                >
                    <span class="sr-only">Close</span>
                    <IconX class="size-3.5" />
                </button>
            </div>

            <ul class="flex flex-col gap-2">
                <li v-for="clip in clips" :key="clip.video" data-reveal>
                    <button
                        type="button"
                        class="flex h-46 w-full cursor-pointer flex-col overflow-hidden text-left"
                        @click="openClip(clip)"
                    >
                        <div
                            class="relative flex flex-1 items-center justify-center overflow-hidden"
                        >
                            <img
                                :src="clip.thumb"
                                :alt="clip.title"
                                class="bg-marine-400 absolute inset-0 size-full object-cover"
                            />
                            <IconPlay class="relative size-10" />
                        </div>
                        <div
                            v-if="clip.title"
                            class="bg-marine-600 flex w-full shrink-0 items-center p-3"
                        >
                            <p
                                class="body-md leading-[0.75] font-bold tracking-[-0.04em] uppercase"
                            >
                                {{ clip.title }}
                            </p>
                        </div>
                    </button>
                </li>
            </ul>
        </div>
    </Transition>
</template>

<style scoped>
.behind-the-scenes {
    grid-column: 1 / 7;
    grid-row: 2 / -1;
    z-index: 4;
    /* Constrain to the grid track (overriding the default min-height: auto of a
       grid item) so the panel's overflow-y-auto can actually scroll the clips. */
    min-height: 0;
}

/* On mobile the panel fills the globe area between the header (row 1) and the
   Extras footer. A bottom margin equal to the footer's height shrinks the panel
   (a stretched grid item fills its area minus margins) so it ends exactly at the
   top of the footer — keeping the footer visible and the full clip list within
   the panel's scrollable height. */
@media (max-width: 1024px) {
    .behind-the-scenes {
        grid-column: 1 / -1;
        grid-row: 2 / -1;
        z-index: 2;
        margin-bottom: var(--mobile-controls-h, 240px);
    }
}
</style>
